/* ============================================
   ScheduleDatePicker — Popover date/time picker
   for scheduling messages in the future
   ============================================ */
import { useState, useRef, useEffect, useCallback } from 'react'
import { IconClock } from '@/components/icons/Icons'

interface ScheduleDatePickerProps {
  onSchedule: (scheduledAt: Date) => void
  onClose: () => void
}

/** Get tomorrow at 9:00 AM in America/Lima timezone */
function getDefaultDateTime(): { date: string; time: string } {
  const now = new Date()
  // Tomorrow
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000)
  // Format as YYYY-MM-DD
  const year = tomorrow.getFullYear()
  const month = String(tomorrow.getMonth() + 1).padStart(2, '0')
  const day = String(tomorrow.getDate()).padStart(2, '0')
  return { date: `${year}-${month}-${day}`, time: '09:00' }
}

/** Format date for preview display */
function formatPreview(dateStr: string, timeStr: string): string {
  if (!dateStr || !timeStr) return ''

  const [year, month, day] = dateStr.split('-').map(Number)
  const [hours, minutes] = timeStr.split(':').map(Number)
  const d = new Date(year, month - 1, day, hours, minutes)

  const weekday = d.toLocaleDateString('es-PE', { weekday: 'short' })
  const monthName = d.toLocaleDateString('es-PE', { month: 'short' })
  const dayNum = d.getDate()
  const yearNum = d.getFullYear()

  // 12h format
  const h12 = hours % 12 || 12
  const ampm = hours < 12 ? 'AM' : 'PM'
  const mins = String(minutes).padStart(2, '0')

  // Capitalize first letter of weekday
  const cap = weekday.charAt(0).toUpperCase() + weekday.slice(1)

  return `${cap} ${dayNum} ${monthName} ${yearNum}, ${h12}:${mins} ${ampm}`
}

/** Get today's date as YYYY-MM-DD */
function getTodayStr(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

export default function ScheduleDatePicker({ onSchedule, onClose }: ScheduleDatePickerProps) {
  const defaults = getDefaultDateTime()
  const [date, setDate] = useState(defaults.date)
  const [time, setTime] = useState(defaults.time)
  const [error, setError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    // Delay to avoid closing immediately on the same click
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick)
    }, 10)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleClick)
    }
  }, [onClose])

  const validate = useCallback((): Date | null => {
    if (!date || !time) {
      setError('Selecciona fecha y hora')
      return null
    }

    const [year, month, day] = date.split('-').map(Number)
    const [hours, minutes] = time.split(':').map(Number)
    const scheduled = new Date(year, month - 1, day, hours, minutes)

    const minTime = new Date(Date.now() + 2 * 60 * 1000) // now + 2 min
    if (scheduled <= minTime) {
      setError('La fecha debe ser al menos 2 minutos en el futuro')
      return null
    }

    setError(null)
    return scheduled
  }, [date, time])

  const handleSubmit = () => {
    const scheduled = validate()
    if (scheduled) {
      onSchedule(scheduled)
    }
  }

  const preview = formatPreview(date, time)

  return (
    <div className="schedule-picker" ref={containerRef}>
      <div className="schedule-picker__header">
        <IconClock size={16} />
        <span>Programar mensaje</span>
      </div>

      <div className="schedule-picker__fields">
        <input
          type="date"
          className="schedule-picker__input"
          value={date}
          min={getTodayStr()}
          onChange={(e) => { setDate(e.target.value); setError(null) }}
        />
        <input
          type="time"
          className="schedule-picker__input"
          value={time}
          onChange={(e) => { setTime(e.target.value); setError(null) }}
        />
      </div>

      {preview && (
        <div className="schedule-picker__preview">
          {preview}
        </div>
      )}

      {error && (
        <div className="schedule-picker__error">
          {error}
        </div>
      )}

      <button
        className="schedule-picker__btn"
        onClick={handleSubmit}
        type="button"
      >
        Programar envío
      </button>
    </div>
  )
}
